import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../config';
import StudentJoinGame from './StudentJoinGame';

// Class Selector Component with Expandable Years
const ClassSelector = ({ value, onChange }) => {
  const [expandedYear, setExpandedYear] = useState(null);

  const yearStructure = {
    'Pre School': ['PSA', 'PSB', 'PSC'],
    'Reception': ['RA', 'RB', 'RC'],
    'Year 1': ['1A', '1B', '1C', '1D'],
    'Year 2': ['2A', '2B', '2C', '2D'],
    'Year 3': ['3A', '3B', '3C', '3D'],
    'Year 4': ['4A', '4B', '4C', '4D'],
    'Year 5': ['5A', '5B', '5C', '5D'],
    'Year 6': ['6A', '6B', '6C', '6D']
  };

  return (
    <div className="space-y-2 border-2 border-gray-200 rounded-xl p-3 bg-gray-50 max-h-64 overflow-y-auto">
      {Object.entries(yearStructure).map(([year, forms]) => (
        <div key={year}>
          {/* Year Header (Expandable) */}
          <button
            onClick={() => setExpandedYear(expandedYear === year ? null : year)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all font-semibold ${
              expandedYear === year
                ? 'bg-amber-100 text-amber-900'
                : 'bg-white text-gray-900 hover:bg-amber-50'
            }`}
          >
            <span>{year}</span>
            <span className={`transform transition-transform ${expandedYear === year ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </button>

          {/* Expandable Forms */}
          {expandedYear === year && (
            <div className="mt-2 ml-4 space-y-2 border-l-2 border-amber-200 pl-4">
              {forms.map(form => (
                <button
                  key={form}
                  onClick={() => onChange(form)}
                  className={`w-full text-left px-4 py-2 rounded-lg transition-all ${
                    value === form
                      ? 'bg-amber-500 text-white font-semibold'
                      : 'bg-white text-gray-700 hover:bg-amber-100 border border-gray-200'
                  }`}
                >
                  {form}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
      
      {value && (
        <div className="mt-3 p-3 bg-amber-100 rounded-lg border border-amber-300">
          <p className="text-sm text-amber-900">
            <strong>Selected:</strong> {value}
          </p>
        </div>
      )}
    </div>
  );
};

// Racing Car Game Component (Racing Game)
const RacingGameComponent = ({ teacherEmail, teacherName, onBack }) => {
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQuestionReview, setShowQuestionReview] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [gameSettings, setGameSettings] = useState({
    class_form: '',
    difficulty: 'medium',
    question_count: 10,
    use_ai: false,
    ai_prompt: ''
  });
  const [creating, setCreating] = useState(false);
  const [starting, setStarting] = useState(false);
  const [gameState, setGameState] = useState(null);
  const [ws, setWs] = useState(null);

  // Websocket connection for real-time updates
  useEffect(() => {
    if (selectedGame) {
      connectWebSocket(selectedGame.game_id);
    }
    
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [selectedGame]);

  const connectWebSocket = (gameId) => {
    const wsUrl = buildApiUrl('/ws/racing-game/' + gameId).replace('http', 'ws');
    const websocket = new WebSocket(wsUrl);

    websocket.onopen = () => {
      console.log('🎮 WebSocket connected to game:', gameId);
      // Request initial state
      websocket.send(JSON.stringify({ type: 'request_update' }));
    };

    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('📡 WebSocket message:', data);
      
      if (data.type === 'game_state' || data.type === 'game_update') {
        setGameState(data.data.game_state || data.data);
      }
    };

    websocket.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
    };

    websocket.onclose = () => {
      console.log('🔌 WebSocket disconnected');
    };

    setWs(websocket);
  };

  const handleCreateGame = async () => {
    if (!gameSettings.class_form) {
      alert('Please select a class');
      return;
    }

    setCreating(true);

    try {
      let questions = [];

      if (gameSettings.use_ai && gameSettings.ai_prompt) {
        // Generate questions using AI
        const aiResponse = await fetch(buildApiUrl('/api/racing-game/generate-questions-ai'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: gameSettings.ai_prompt,
            count: gameSettings.question_count
          })
        });

        const aiData = await aiResponse.json();
        if (aiData.success) {
          questions = aiData.questions;
        }
      }

      // Show question review modal for teacher to edit/confirm
      setGeneratedQuestions(questions);
      setShowQuestionReview(true);
      setShowCreateModal(false);
    } catch (error) {
      console.error('❌ Error generating questions:', error);
      alert('Error generating questions');
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmAndCreateGame = async (finalQuestions) => {
    try {
      setCreating(true);

      // Create game
      const response = await fetch(buildApiUrl('/api/racing-game/create'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teacher_id: teacherEmail,
          class_form: gameSettings.class_form,
          difficulty: gameSettings.difficulty,
          question_count: gameSettings.question_count
        })
      });

      const data = await response.json();

      if (data.success) {
        const newGame = {
          game_id: data.game_id,
          class_form: gameSettings.class_form,
          questions: finalQuestions || data.questions,
          status: 'waiting',
          players: []
        };
        
        setGames([...games, newGame]);
        setSelectedGame(newGame);
        setShowQuestionReview(false);
        
        // Reset settings
        setGameSettings({
          class_form: '',
          difficulty: 'medium',
          question_count: 10,
          use_ai: false,
          ai_prompt: ''
        });
        setGeneratedQuestions([]);
      } else {
        alert('Failed to create game: ' + data.error);
      }
    } catch (error) {
      console.error('❌ Error creating game:', error);
      alert('Error creating game');
    } finally {
      setCreating(false);
    }
  };

  const handleStartGame = async () => {
    if (!selectedGame || starting) return;

    setStarting(true);
    try {
      const response = await fetch(buildApiUrl(`/api/racing-game/start?game_id=${selectedGame.game_id}`), {
        method: 'POST'
      });

      const data = await response.json();

      if (data.success) {
        // Backend already broadcasts game_started via WebSocket
        // Update local state with game state
        setGameState(data.game_state);
      } else {
        alert('Failed to start game: ' + data.error);
      }
    } catch (error) {
      console.error('❌ Error starting game:', error);
      alert('Error starting game');
    } finally {
      setStarting(false);
    }
  };

  const handleDeleteGame = async (gameId) => {
    if (!confirm('Are you sure you want to delete this game?')) return;

    try {
      const response = await fetch(buildApiUrl(`/api/racing-game/${gameId}`), {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        setGames(games.filter(g => g.game_id !== gameId));
        if (selectedGame?.game_id === gameId) {
          setSelectedGame(null);
        }
      }
    } catch (error) {
      console.error('❌ Error deleting game:', error);
    }
  };

  if (selectedGame) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50">
        {/* Header */}
        <div className="bg-white shadow-md border-b">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => {
                    setSelectedGame(null);
                    if (ws) ws.close();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <span className="text-2xl">←</span>
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    🏎️ Racing Game - {selectedGame.class_form}
                  </h1>
                  <p className="text-gray-600 text-sm mt-1">
                    Game ID: {selectedGame.game_id} • {gameState?.total_questions || 0} Questions
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {gameState?.status === 'waiting' && (
                  <button
                    onClick={handleStartGame}
                    disabled={starting}
                    className={`px-6 py-3 ${starting ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'} text-white rounded-xl transition-all shadow-lg font-semibold`}
                  >
                    {starting ? '⏳ Starting...' : '🚦 Start Race'}
                  </button>
                )}
                
                <div className={`px-4 py-2 rounded-lg font-semibold ${
                  gameState?.status === 'waiting' ? 'bg-yellow-100 text-yellow-700' :
                  gameState?.status === 'active' ? 'bg-green-100 text-green-700 animate-pulse' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {gameState?.status === 'waiting' ? '⏳ Waiting for Players' :
                   gameState?.status === 'active' ? '🏁 Race Active!' :
                   '✅ Finished'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Game Content */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Join Instructions */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl p-6 border-2 border-amber-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>📱</span> Students Can Join
              </h2>
              
              <div className="bg-gradient-to-br from-amber-100 to-yellow-100 rounded-xl p-6 mb-6">
                <p className="text-gray-700 mb-2">Share this Game ID with your students:</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-white rounded-lg p-4 text-center">
                    <p className="text-4xl font-bold text-amber-600 tracking-wider">{selectedGame.game_id}</p>
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedGame.game_id);
                      alert('Game ID copied!');
                    }}
                    className="px-4 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition font-semibold"
                  >
                    📋 Copy
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-3">
                  Students can join from their dashboard by entering this code
                </p>
              </div>

              {/* Racing Track Visualization */}
              <div className="bg-gradient-to-b from-gray-100 to-gray-200 rounded-xl p-6 border-2 border-gray-300">
                <h3 className="text-lg font-bold text-gray-900 mb-4">🏁 Race Progress</h3>
                
                {gameState?.players && gameState.players.length > 0 ? (
                  <div className="space-y-4">
                    {gameState.players.map((player, idx) => {
                      const progress = (player.progress / (gameState.total_questions || 10)) * 100;
                      const cars = ['🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐'];
                      
                      return (
                        <div key={player.student_id} className="relative">
                          {/* Track */}
                          <div className="bg-white rounded-lg border-2 border-gray-300 h-16 relative overflow-hidden">
                            {/* Finish Line */}
                            <div className="absolute right-0 top-0 bottom-0 w-2 bg-gradient-to-r from-red-500 to-yellow-500 animate-pulse"></div>
                            
                            {/* Car */}
                            <div
                              className="absolute top-0 bottom-0 flex items-center transition-all duration-1000 ease-out"
                              style={{ left: `${Math.min(progress, 95)}%` }}
                            >
                              <div className="text-4xl transform hover:scale-125 transition-transform">
                                {cars[player.car_id - 1] || '🏎️'}
                              </div>
                            </div>
                          </div>
                          
                          {/* Player Info */}
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-sm font-semibold text-gray-700">{player.full_name}</span>
                            <span className="text-sm text-gray-600">
                              {player.progress}/{gameState.total_questions} 
                              {player.finished && <span className="ml-2 text-green-600 font-bold">✓ FINISHED!</span>}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-4xl mb-4">🏁</p>
                    <p className="text-gray-500">Waiting for students to join...</p>
                  </div>
                )}
              </div>
            </div>

            {/* Leaderboard */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-amber-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>🏆</span> Leaderboard
              </h2>
              
              {gameState?.players && gameState.players.length > 0 ? (
                <div className="space-y-3">
                  {[...gameState.players]
                    .sort((a, b) => b.progress - a.progress)
                    .map((player, idx) => (
                      <div
                        key={player.student_id}
                        className={`p-4 rounded-xl border-2 ${
                          idx === 0 ? 'bg-gradient-to-br from-yellow-100 to-amber-100 border-yellow-400' :
                          idx === 1 ? 'bg-gradient-to-br from-gray-100 to-gray-200 border-gray-400' :
                          idx === 2 ? 'bg-gradient-to-br from-orange-100 to-red-100 border-orange-400' :
                          'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="text-2xl font-bold text-gray-700">
                            {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">{player.full_name}</p>
                            <p className="text-sm text-gray-600">{player.progress} questions</p>
                          </div>
                          {player.finished && (
                            <span className="text-green-600 text-xl">✓</span>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-400">No players yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50">
      {/* Header */}
      <div className="bg-white shadow-md border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <span className="text-2xl">←</span>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">🏎️ Education Games</h1>
                <p className="text-gray-600 text-sm mt-1">Create interactive racing games for your students</p>
              </div>
            </div>
            
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-amber-600 to-yellow-600 text-white rounded-xl hover:from-amber-700 hover:to-yellow-700 transition-all shadow-lg font-semibold flex items-center gap-2"
            >
              <span>➕</span> Create New Game
            </button>
          </div>
        </div>
      </div>

      {/* Games List */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {games.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {games.map((game) => (
              <div
                key={game.game_id}
                onClick={() => setSelectedGame(game)}
                className="bg-white rounded-2xl shadow-xl p-6 border-2 border-amber-200 hover:border-amber-400 cursor-pointer transition-all hover:scale-105"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-xl flex items-center justify-center text-3xl">
                    🏎️
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteGame(game.game_id);
                    }}
                    className="p-2 hover:bg-red-100 rounded-lg transition text-red-600"
                  >
                    🗑️
                  </button>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-2">{game.class_form}</h3>
                <p className="text-gray-600 text-sm mb-4">Game ID: {game.game_id}</p>
                
                <div className="flex items-center justify-between">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    game.status === 'waiting' ? 'bg-yellow-100 text-yellow-700' :
                    game.status === 'active' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {game.status || 'waiting'}
                  </span>
                  <span className="text-sm text-gray-600">
                    {game.players?.length || 0} players
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center border-2 border-amber-200">
            <div className="w-24 h-24 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-2xl flex items-center justify-center text-5xl mx-auto mb-6">
              🏎️
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Games Yet</h2>
            <p className="text-gray-600 mb-6">Create your first racing game to get started!</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-8 py-4 bg-gradient-to-r from-amber-600 to-yellow-600 text-white rounded-xl hover:from-amber-700 hover:to-yellow-700 transition-all shadow-lg font-semibold"
            >
              ➕ Create New Game
            </button>
          </div>
        )}
      </div>

      {/* Question Review Modal */}
      {showQuestionReview && generatedQuestions.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <h2 className="text-2xl font-bold text-gray-900">Review Generated Questions</h2>
              <p className="text-gray-600 mt-1">Edit questions if needed before creating the game</p>
            </div>
            
            <div className="p-6 space-y-4">
              {generatedQuestions.map((question, idx) => (
                <div key={idx} className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-sm text-amber-700 font-semibold">Question {idx + 1}</p>
                      <p className="text-lg font-semibold text-gray-900 mt-1">{question.question}</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Options:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {question.options.map((option, optIdx) => (
                        <div
                          key={optIdx}
                          className={`p-3 rounded-lg border-2 ${
                            option === question.answer
                              ? 'bg-green-100 border-green-400'
                              : 'bg-white border-gray-200'
                          }`}
                        >
                          <span className="text-gray-900">{option}</span>
                          {option === question.answer && (
                            <span className="ml-2 text-green-700 font-bold">✓ Correct</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 border-t sticky bottom-0 bg-white flex gap-3">
              <button
                onClick={() => {
                  setShowQuestionReview(false);
                  setGeneratedQuestions([]);
                  setShowCreateModal(true);
                }}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition font-semibold"
              >
                Back
              </button>
              <button
                onClick={() => handleConfirmAndCreateGame(generatedQuestions)}
                disabled={creating}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg font-semibold disabled:opacity-50"
              >
                {creating ? '⏳ Creating Game...' : '✅ Create Game with These Questions'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Game Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">Create New Racing Game</h2>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Class Selection with Expandable Years */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Class
                </label>
                <ClassSelector 
                  value={gameSettings.class_form} 
                  onChange={(value) => setGameSettings({...gameSettings, class_form: value})}
                />
              </div>

              {/* Difficulty */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Difficulty Level
                </label>
                <select
                  value={gameSettings.difficulty}
                  onChange={(e) => setGameSettings({...gameSettings, difficulty: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition"
                >
                  <option value="easy">Easy (1-20, +/-)</option>
                  <option value="medium">Medium (5-50, +/-/×)</option>
                  <option value="hard">Hard (10-100, all operations)</option>
                </select>
              </div>

              {/* Question Count */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Questions: {gameSettings.question_count}
                </label>
                <input
                  type="range"
                  min="5"
                  max="20"
                  value={gameSettings.question_count}
                  onChange={(e) => setGameSettings({...gameSettings, question_count: parseInt(e.target.value)})}
                  className="w-full"
                />
              </div>

              {/* AI Generation */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={gameSettings.use_ai}
                    onChange={(e) => setGameSettings({...gameSettings, use_ai: e.target.checked})}
                    className="w-5 h-5 text-amber-600"
                  />
                  <div>
                    <span className="font-semibold text-gray-900">Use AI to Generate Questions</span>
                    <p className="text-sm text-gray-600">Create custom questions based on your requirements</p>
                  </div>
                </label>
                
                {gameSettings.use_ai && (
                  <textarea
                    value={gameSettings.ai_prompt}
                    onChange={(e) => setGameSettings({...gameSettings, ai_prompt: e.target.value})}
                    placeholder="E.g., 'Create multiplication questions for times tables 6-9' or 'Focus on fractions with denominators 2, 4, and 8'"
                    className="w-full mt-4 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition resize-none"
                    rows={4}
                  />
                )}
              </div>
            </div>

            <div className="p-6 border-t flex gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateGame}
                disabled={creating || !gameSettings.class_form}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-600 to-yellow-600 text-white rounded-xl hover:from-amber-700 hover:to-yellow-700 transition-all shadow-lg font-semibold disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Game'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Education Games Dashboard Component
const EducationGames = ({ teacherEmail, teacherName, onBack }) => {
  const [selectedGame, setSelectedGame] = useState(null);

  // Check if this is a student or teacher based on localStorage role
  const userRole = localStorage.getItem('userRole') || 'student';
  const isStudent = userRole === 'student';

  // For students, show the join game interface
  if (isStudent) {
    // Get student email/name from localStorage or props
    const studentEmail = teacherEmail || localStorage.getItem('userEmail') || 'student@school.com';
    const studentName = teacherName || localStorage.getItem('userName') || 'Student';
    
    return (
      <StudentJoinGame 
        studentEmail={studentEmail}
        studentName={studentName}
        onBack={onBack}
      />
    );
  }

  // For teachers, show the game selection dashboard and creation interface
  const games = [
    {
      id: 'racing-car',
      title: '🏎️ Racing Car Game',
      description: 'Students answer questions to move their cars towards the finish line. First to answer all questions wins!',
      icon: '🏎️',
      color: 'from-orange-500 to-red-600',
      features: ['Real-time multiplayer', 'AI question generation', 'Leaderboard', 'Difficulty levels']
    },
    {
      id: 'spelling-bee',
      title: '✏️ Spelling Bee',
      description: 'Coming soon - Students compete in spelling challenges',
      icon: '✏️',
      color: 'from-blue-500 to-cyan-600',
      features: ['Word suggestions', 'Difficulty levels', 'Progress tracking'],
      comingSoon: true
    },
    {
      id: 'trivia-quiz',
      title: '🧠 Trivia Quiz',
      description: 'Coming soon - Multiple choice questions with instant feedback',
      icon: '🧠',
      color: 'from-purple-500 to-pink-600',
      features: ['Multiple choice', 'Timed rounds', 'Category selection'],
      comingSoon: true
    },
    {
      id: 'math-marathon',
      title: '🔢 Math Marathon',
      description: 'Coming soon - Speed-based math challenges',
      icon: '🔢',
      color: 'from-green-500 to-emerald-600',
      features: ['Timed rounds', 'Leaderboard', 'Difficulty progression'],
      comingSoon: true
    }
  ];

  if (selectedGame === 'racing-car') {
    return <RacingGameComponent 
      teacherEmail={teacherEmail} 
      teacherName={teacherName} 
      onBack={() => setSelectedGame(null)} 
    />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="mb-4 text-amber-700 hover:text-amber-900 font-semibold flex items-center gap-2"
          >
            ← Back to Dashboard
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">🎮 Education Games</h1>
          <p className="text-gray-600">Interactive games to engage and assess your students</p>
        </div>

        {/* Games Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {games.map(game => (
            <div
              key={game.id}
              className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all ${
                game.comingSoon ? 'opacity-70' : 'cursor-pointer hover:scale-105'
              }`}
            >
              <div className={`bg-gradient-to-r ${game.color} p-8 text-white rounded-t-2xl`}>
                <div className="text-5xl mb-3">{game.icon}</div>
                <h2 className="text-2xl font-bold">{game.title}</h2>
              </div>

              <div className="p-6">
                <p className="text-gray-600 mb-4">{game.description}</p>

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-2">Features:</h4>
                  <ul className="space-y-1">
                    {game.features.map((feature, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-center">
                        <span className="text-amber-500 mr-2">✓</span> {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => setSelectedGame(game.id)}
                  disabled={game.comingSoon}
                  className={`w-full py-3 rounded-xl font-semibold transition-all ${
                    game.comingSoon
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r ' + game.color + ' text-white hover:shadow-lg transform hover:scale-105'
                  }`}
                >
                  {game.comingSoon ? 'Coming Soon' : 'Play Game'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Info Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-lg p-8 border-l-4 border-amber-500">
          <h3 className="text-xl font-bold text-gray-900 mb-4">💡 How Education Games Work</h3>
          <ul className="space-y-2 text-gray-600">
            <li>✨ Create game sessions with your class</li>
            <li>🎯 Students answer questions and compete in real-time</li>
            <li>📊 Track progress and see leaderboards</li>
            <li>🤖 Use AI to generate questions automatically</li>
            <li>🏆 Motivate students with interactive gameplay</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EducationGames;
